import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRequirements, fetchRequirementStats } from '../../store/slices/hrSlice';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import { HR_REQUIREMENT_STATUS } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';
import { useNavigate } from 'react-router-dom';

const HRDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requirements, requirementStats, pagination, loading } = useSelector((state) => state.hr);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', search: '' });

  useEffect(() => {
    dispatch(fetchRequirements({ page, ...filters }));
    dispatch(fetchRequirementStats());
  }, [dispatch, page, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setPage(1);
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <h1>HR Dashboard</h1>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/hr/requirements/new')}
          >
            <i className="ti ti-plus me-1" />
            Create Requirement
          </button>
        </div>

        {/* Stats Cards */}
        {requirementStats && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Requirements</h3>
              <p className="stat-value">{requirementStats.total}</p>
            </div>
            {Object.entries(requirementStats.by_status || {}).map(([status, count]) => (
              <div key={status} className="stat-card">
                <h3><StatusBadge status={status} /></h3>
                <p className="stat-value">{count}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Status</label>
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  {Object.values(HR_REQUIREMENT_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Search</label>
                <input
                  type="text"
                  name="search"
                  className="form-control"
                  placeholder="Search by title or department"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Requirements Table */}
        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table mb-0 text-nowrap table-hover">
                <thead className="table-light border-light">
                  <tr>
                    <th>Title</th>
                    <th>Department</th>
                    <th>Experience</th>
                    <th>Openings</th>
                    <th>Status</th>
                    <th>Assigned HR</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requirements.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4 text-muted">
                        No requirements found
                      </td>
                    </tr>
                  ) : (
                    requirements.map((req) => (
                      <tr key={req._id} className="align-middle">
                        <td className="fw-medium">{req.title}</td>
                        <td>{req.department}</td>
                        <td>{req.experience_required}</td>
                        <td>{req.number_of_openings}</td>
                        <td><StatusBadge status={req.status} /></td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            {(Array.isArray(req.assigned_hr) ? req.assigned_hr : [req.assigned_hr]).filter(Boolean).map((emp, idx) => (
                              <span key={idx} className="badge bg-primary" style={{ fontSize: '11px' }}>
                                {emp.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>{formatDateTime(req.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/hr/requirements/${req._id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination pagination={pagination} onPageChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default HRDashboard;
